import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

import MenuBar from './../components/Menubar';
import ItemNew from './../components/ItemNew';
import LoaderContext from './../components/LoaderContext';
import LoginPage from './LoginPage';
import { Link } from 'react-router-dom';
import { useCravings } from '../hooks/useCravings';
import MapImage from '../images/map_logo.png';

type Props = {
  user: any;
  restaurantPage: Boolean;
  handleLogin(user): void;
};

export const generateDollarSigns = (count: number): string => {
  return '$'.repeat(count);
};

const to12h = (t?: string | null) => {
  if (!t) return '';
  const [hh, mm] = t.split(':');
  let h = parseInt(hh, 10);
  const m = (mm ?? '00').slice(0, 2);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
};

const formatRange = (o?: string | null, c?: string | null, closed?: boolean) => {
  if (closed) return 'Closed';
  if (!o || !c) return '—';
  return `${to12h(o)} – ${to12h(c)}`;
};

const handleMainFilter = (value: boolean) => {};

const RestaurantPage: React.FC<Props> = (props) => {
  const [error, setError] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [restaurant, setRestaurant] = useState<any>({});
 const [showLogin, setShowLogin] = useState(false);
const { cravedIds, handleToggleCraving } = useCravings(props.user); // craving update: state to show login prompt when logged-out user clicks heart
 const [showCravingPrompt, setShowCravingPrompt] = useState(false);

  const { id }: any = useParams();
  const { showLoader, hideLoader } = useContext(LoaderContext);
  const [showJumpToTopButton, setShowJumpToTopButton] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [showWebsiteNotFound, setShowWebsiteNotFound] = useState(false);
  let didCancel = false;

  const HOURS =
    Array.isArray(restaurant?.openingHours) && restaurant.openingHours.length ? restaurant.openingHours : [];

  const websiteUrl = restaurant?.website || restaurant?.websiteUrl || restaurant?.url || null;
  const phone = restaurant?.phone || restaurant?.contactNumber || 'NOT FOUND';



  const log = async (message: any) => {
    if (process.env.REACT_APP_LOG === 'true') {
      console.log(message);
    }
  };

  const loadData = async () => {
    showLoader();
    setIsLoaded(false);

    const headers: any = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (props.user.isLogged) {
      headers['Authorization'] = 'Bearer ' + props.user.token;
    }

    axios
      .get(process.env.REACT_APP_SERVER_URL + '/restaurants/' + id + '?v=' + Date.now(), {
        headers: headers,
      })
      .then((result) => {
        if (!didCancel) {
          setItems(result.data.items || []);
          setRestaurant(result.data.restaurant || {});
          setError('');
          setIsLoaded(true);
          hideLoader();
        }
      })
      .catch((err) => {
        log(err);
        if (!didCancel) {
          setError('Network error!');
          setIsLoaded(false);
          hideLoader();
        }
      });
  };

  useEffect(() => {
    loadData();
    return () => {
      didCancel = true;
    };
  }, [props.user.isLogged]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle scroll for Jump to Top button
  const handleScroll = () => {
    const windowHeight = window.innerHeight;
    const scrollPosition = window.scrollY;
    const scrollThreshold = windowHeight * 1;
    setShowJumpToTopButton(scrollPosition > scrollThreshold);
  };

  const handleJumpToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpvote = async (id) => {
    showLoader();
    const data = { userId: props.user.id };
    fetch(process.env.REACT_APP_SERVER_URL + '/items/' + id + '/upvote', {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: 'Bearer ' + props.user.token,
      }),
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then(
        (result) => {
          log(result);
          setItems(
            items.map((item) => (item.id === id ? { ...item, upvoted: true, upvoteCount: item.upvoteCount + 1 } : item))
          );
          setError('');
          hideLoader();
        },
        (error) => {
          log(error);
          setError(error.message);
          hideLoader();
        }
      );
  };
  const handleRemoveUpvote = async (_id: number) => {};


  const onLoginClick = async () => setShowLogin(true);
  const onLoginBackClick = async () => setShowLogin(false);
  // craving update: show middle page when logged-out user clicks heart
  const handleLoginPrompt = () => setShowCravingPrompt(true);
  const onCravingPromptBackClick = () => setShowCravingPrompt(false);
  const onCallClick = () => setShowPhone((prev) => !prev);

  const onWebsiteClick = (e: React.MouseEvent) => {
    if (websiteUrl === 'NOT FOUND') {
      e.preventDefault();
      setShowWebsiteNotFound((prev) => !prev);
    } else {
      setShowWebsiteNotFound(false);
    }
  };

  const sendRestaurantNotification = (restaurantId: number) => {
    try {
      const stored = localStorage.getItem('YoloMeal');
      if (stored) {
        showLoader();
        const userData = JSON.parse(stored);
        fetch(
          process.env.REACT_APP_SERVER_URL +
            `/restaurant/markClosed?restaurantId=${restaurantId}&userId=${userData.id}`,
          {
            method: 'GET',
            headers: new Headers({
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: 'Bearer ' + userData.token,
            }),
          }
        ).then(
          () => {
            setRestaurant((r: any) => ({ ...r, restaurantClosed: true }));
            hideLoader();
          },
          () => hideLoader()
        );
      }
    } catch (error) {
      console.error('Error sending restaurant notification:', error);
    }
  };

  const handleRestaurantClick = async (restaurantId: number) => {
    sendRestaurantNotification(restaurantId);
  };

  const sendNotification = (itemId: number) => {
    try {
      const stored = localStorage.getItem('YoloMeal');
      if (stored) {
        showLoader();
        const userData = JSON.parse(stored);
        fetch(process.env.REACT_APP_SERVER_URL + `/itemExpired?itemId=${itemId}&userId=${userData.id}`, {
          method: 'GET',
          headers: new Headers({
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: 'Bearer ' + userData.token,
          }),
        }).then(
          () => {
            setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, expired: true } : it)));
            hideLoader();
          },
          () => hideLoader()
        );
      }
    } catch (error) {
      console.error('Error notifying item expired:', error);
    }
  };

  const url =
    'https://www.google.com/maps/search/?api=1&query=' +
    encodeURI(restaurant?.name || '') +
    '&query_place_id=' +
    (restaurant?.placeId || '');

  // craving update: middle page for logged-out heart click
  if (showCravingPrompt && !(props.user as any).isLogged) {
      return (
          <main className="main-bg">
              <div className="container mypage" style={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                  <MenuBar user={props.user} />
                  <section style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div className="page-box-main text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
                          <h1 className="font-reklame" style={{ margin: 0 }}>
                              You must be signed in to save cravings
                          </h1>
                          <Link className="tap-to-upvote-btn upvote_btn pill-std" to="/login">
                              Log in
                          </Link>
                          <a className="tap-to-upvote-btn upvote_btn pill-std" style={{ cursor: 'pointer' }} onClick={onCravingPromptBackClick}>
                              Go back
                          </a>
                      </div>
                  </section>
              </div>
          </main>
      );
  }

  if (showLogin && !(props.user as any).isLogged) {
    return (
      <LoginPage
        user={props.user}
        handleLogin={props.handleLogin}
        showBack={true}
        showHome={false}
        backgroundImage={true}
        handleBack={onLoginBackClick}
      />
    );
  }

  return (
    <main className="main-bg address-bg restaurant-page">
      <div className="small-container ">
        <MenuBar user={props.user} />
        <div className="address-info" style={{ marginBottom: '20px' }}>
          {isLoaded && (
            <>
              <div className="map-box pb-4">
                <div className="restaurant-box bg-white">
                  <div className="restaurant-summary">
                    <p className="restaurant-name">
                      {restaurant.name} {generateDollarSigns(restaurant.priceLevel || 0)}
                    </p>
                    <p className="restaurant-address">{restaurant.address}</p>
                  </div>

                  {props.user.isLogged && props.restaurantPage && !restaurant.restaurantClosed && (
                    <a
                      className="tap-to-closed-btn"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRestaurantClick(restaurant.id)}
                    >
                      Restaurant Permanently Closed?
                    </a>
                  )}

                  {props.user.isLogged && props.restaurantPage && restaurant.restaurantClosed && (
                    <a className="tap-to-thanks-btn disabled" style={{ cursor: 'pointer' }}>
                      Thanks for reporting
                    </a>
                  )}
                </div>
              </div>

              <div className="action-grid">
                <div className="action-cell">
                  {showWebsiteNotFound && (
                    <span
                      id="url-reveal"
                      className="url-reveal"
                      role="status"
                      aria-live="polite"
                      title="Tap Call again to copy"
                    >
                      {websiteUrl}
                    </span>
                  )}

                  <a className="action-btn" href={websiteUrl} target="_blank" rel="noreferrer" onClick={onWebsiteClick}>
                    Website
                  </a>
                </div>

                <div className="action-cell">
                  {showPhone && (
                    <span
                      id="phone-reveal"
                      className="phone-reveal"
                      role="status"
                      aria-live="polite"
                      title="Tap Call again to copy"
                    >
                      {phone}
                    </span>
                  )}

                  <button
                    type="button"
                    className="action-btn"
                    onClick={onCallClick}
                    aria-expanded={showPhone}
                    aria-controls="phone-reveal"
                  >
                    Call
                  </button>
                </div>

                <div className="action-cell">
                  <a className="icon-link" href={url} target="_blank" rel="noreferrer" aria-label="Open in Google Maps">
                    <img src={MapImage} alt="" />
                  </a>
                </div>
              </div>

              <div className="hours-card" style={{ maxWidth: 520, margin: '0 auto' }}>
                <h3 style={{ fontSize: 14, letterSpacing: 1, marginLeft: '-60px' }}>HOURS</h3>
                <ul className="hours-list" style={{ fontSize: 14, lineHeight: 1.3, padding: '0 4px' }}>
                  {HOURS.length === 0 ? (
                    <div className="not-found-label">NOT FOUND</div>
                  ) : (
                    <ul className="hours-list" style={{ fontSize: 14, lineHeight: 1.3, padding: '0 4px' }}>
                      {HOURS.map(
                        (
                          h: {
                            day: string;
                            openTime?: string;
                            closeTime?: string;
                            closed?: boolean;
                          },
                          idx: number
                        ) => (
                          <li key={h.day || idx} style={{ padding: '6px 2px' }}>
                            <span className="day" style={{ fontWeight: 600 }}>
                              {h.day}
                            </span>
                            <span className="time" style={{ fontVariantNumeric: 'tabular-nums' as any }}>
                              {formatRange(h.openTime, h.closeTime, h.closed)}
                            </span>
                          </li>
                        )
                      )}
                    </ul>
                  )}
                </ul>
              </div>

              {items.map((item) => (
                <ItemNew
                  key={item.id}
                  handleLogin={onLoginClick}
                  item={item}
                  editable={false}
                  isLogged={props.user.isLogged}
                  user={props.user}
                  page={"restaurantPage"}
                  showRestaurant={false}
                  handleUpvote={handleUpvote}
                  handleRemoveUpvote={handleRemoveUpvote}
                  handleNotificationClick={(id: number) => sendNotification(id)}
                  restaurentPage={true}
                  loadData={loadData}
                  handleMainFilter={handleMainFilter}
                 craved={cravedIds.has(item.id)}
                 handleToggleCraving={handleToggleCraving}


                 handleLoginPrompt={handleLoginPrompt}
                />
              ))}

              {error !== '' && <p>{error}</p>}
            </>
          )}
        </div>
      </div>
      {showJumpToTopButton && (
        <button className="jump-to-top-button" onClick={handleJumpToTop}>
          Jump to Top
        </button>
      )}
    </main>
  );
};

export default RestaurantPage;
